// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo } from 'react';

import { type EvolveApiRoute, type EvolveApiResponse, useEvolveGet, type GetOpts } from '@evolvemep/api-ts';

import { isNotNil } from 'helpers/isNotNil';
import type { AnalyticsChartProps } from 'modules/Shop/Insights/RevitUsage/types';
import { useAnalyticsParams } from 'modules/Shop/Insights/RevitUsage/views/charts/useAnalyticsParams';

export const useAnalyticsChart = <
  Route extends Extract<EvolveApiRoute<'GET'>, `usage-stats/v1/reporting/${string}`>,
  T extends EvolveApiResponse<'GET', Route>,
  R = T,
>(
  url: Route,
  { usedFilters, onDataRealized }: Pick<AnalyticsChartProps<R>, 'usedFilters' | 'onDataRealized'>,
  ...[dataAggregator]: R extends T ? [] : [(data: T) => R]
) => {
  const {
    data: rawData,
    setDefaultOpts,
    loading,
  } = useEvolveGet<Route>(url, {
    lazy: true,
    alertOnError: true,
  });
  const data = useMemo(
    () => (dataAggregator && isNotNil(rawData) ? dataAggregator(rawData as T) : (rawData as R | undefined)),
    [rawData, dataAggregator],
  );

  useEffect(() => {
    if (isNotNil(data) && !loading) {
      onDataRealized?.(data);
    } else {
      onDataRealized?.(undefined);
    }
  }, [data, loading, onDataRealized]);

  const { params, stillResolving } = useAnalyticsParams(usedFilters);

  useEffect(() => {
    setDefaultOpts({
      lazy: stillResolving,
      defaultConfig: { params },
    } as GetOpts<Route>);
  }, [stillResolving, params, setDefaultOpts]);

  return [data, loading || stillResolving] as const;
};
